import { Loader } from '@googlemaps/js-api-loader'
import { Experimental_CssVarsProvider as CssVarsProvider } from '@mui/material/styles'
import { LicenseInfo } from '@mui/x-license-pro'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import maplibregl from 'maplibre-gl'
import moment from 'moment-timezone'
import { SnackbarProvider } from 'notistack'
import { Protocol } from 'pmtiles'
import React from 'react'
import {
  BrowserRouter,
  createRoutesFromChildren, matchRoutes,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType
} from 'react-router-dom'
import pkg from '../package.json'
import DialogProvider from './components/DialogProvider'
import PagesLayout from './components/PagesLayout/PagesLayout'
import ViewByPermission from './components/ViewByPermission'
import { MAP_KEY, MUI_KEY, REACT_APP_SERVER_BRANCH, SENTRY_KEY } from './configs/config'
import { theme } from './configs/mui-themes'
import Page404 from './pages/404/Page404'
import Layout from './pages/Layout'
import Login from './pages/Login/Login'
import Permissions from './pages/permissions/Permissions'
import Rides from './pages/rides/Rides'
import { getUserFromLocalStorage } from './utils/utils-functions'

const loader = new Loader({
  apiKey: MAP_KEY,
  version: 'weekly',
  libraries: ['places']
})

loader.importLibrary('places').then(() => {
  console.log('google places loaded')
}).catch(err => {
  console.error(err)
})

moment.tz.setDefault('America/New_York')
LicenseInfo.setLicenseKey(MUI_KEY)

Sentry.init({
  dsn: SENTRY_KEY,
  integrations: [
    new Sentry.BrowserTracing({
      routingInstrumentation: Sentry.reactRouterV6Instrumentation(
        React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      )
    }),
    new Sentry.Replay()
  ],
  sampleRate: 1.0,
  release: pkg.version,
  environment: REACT_APP_SERVER_BRANCH,
  initialScope: {
    user: getUserFromLocalStorage()
  }
})

const queryClient = new QueryClient()

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

const protocol = new Protocol()
maplibregl.addProtocol('pmtiles', protocol.tile)

const App = () => (
  <QueryClientProvider client={queryClient}>
    <CssVarsProvider theme={theme}>
      <SnackbarProvider maxSnack={6} anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}>
        <BrowserRouter>
          <Layout>
            <SentryRoutes>
              <Route path="login" element={<Login />} />
              <Route path="*" element={<PagesLayout />}>
                <Route path="rides" element={<Rides />} />
                <Route path="*" element={<ViewByPermission permissionKey="permissions.view" />}>
                  <Route path="permissions" element={<Permissions />} />
                </Route>
                <Route path="404" element={<Page404 />} />
                <Route path="*" element={<Navigate to="/404" />} />
              </Route>
            </SentryRoutes>
          </Layout>
        </BrowserRouter>
        <DialogProvider />
        <span className="app-version-number">{pkg.version}</span>
      </SnackbarProvider>
    </CssVarsProvider>
  </QueryClientProvider>
)

export default App
